import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import EnhancedTenantSwitchModal, {
  CompanySwitchData
} from '../EnhancedTenantSwitchModal';
import { getAppUrl, getGlobalApiUrl } from '../../utils/url';
import { getAuthToken, getCompanySessionId } from '../../utils/storage';
import { useUser } from '../../contexts/UserContext';

interface BroadcastWrapperProps {
  children: React.ReactNode;
}

interface BroadcastMessage {
  id?: string | number;
  type: string;
  data: unknown;
  timestamp?: string;
}

interface BroadcastWindow extends Window {
  refreshBroadcastMessages?: () => void;
}

const BroadcastWrapper: React.FC<BroadcastWrapperProps> = ({ children }) => {
  const { userDetails } = useUser();
  const [switchData, setSwitchData] = useState<CompanySwitchData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const tabIdRef = useRef<string>(
    `tab_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
  );
  const lastFetchTimeRef = useRef<number>(0);
  const fetchTimeoutRef = useRef<number | null>(null);

  const processMessage = useCallback(
    (message: BroadcastMessage) => {
      switch (message.type) {
        case 'company_switched':
          if (message.data) {
            const data = message.data as Record<string, unknown>;
            const companySwitchData: CompanySwitchData = {
              user_id: Number(data.user_id) || 0,
              old_company_id: Number(data.old_company_id) || 0,
              new_company_id: Number(data.new_company_id) || 0,
              old_company_name: String(data.old_company_name ?? ''),
              new_company_name: String(data.new_company_name ?? ''),
              message: String(data.message ?? ''),
              user_name: String(data.user_name ?? ''),
              timestamp: String(data.timestamp ?? new Date().toISOString())
            };

            const currentCompanyName = userDetails?.company_name ?? '';
            if (currentCompanyName !== companySwitchData.new_company_name) {
              setSwitchData(companySwitchData);
              setIsModalVisible(true);
            }
          }
          break;
        case 'notification':
        case 'profile_updated':
        case 'refresh_sections':
        default:
          break;
      }
    },
    [userDetails?.company_name]
  );

  const fetchBroadcastMessages = useCallback(async () => {
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 1000) {
      return;
    }

    lastFetchTimeRef.current = now;
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    try {
      const authToken = getAuthToken(); //gitleaks:allow
      if (!authToken) {
        return;
      }

      const companySessionId = getCompanySessionId();
      if (!companySessionId) {
        return;
      }

      if (!userDetails?.company_name) {
        return;
      }

      const params = new URLSearchParams({
        tab_id: tabIdRef.current,
        session_id: companySessionId
      });

      if (lastMessageId) {
        params.append('after_id', lastMessageId);
      }

      const apiUrl = `${getGlobalApiUrl('/api/v1/broadcast/')}?${params.toString()}`;

      try {
        const local = window.localStorage;
        local.setItem('broadcast_api_call_attempt', Date.now().toString());
        local.setItem('broadcast_api_call_requestId', requestId);
      } catch (error) {
        console.error(`[${requestId}] Could not record API call attempt:`, error);
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 10000);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      window.clearTimeout(timeoutId);

      if (response.status === 401) {
        console.error(`[${requestId}] Authentication failed`);
        window.location.href = getAppUrl();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: { messages?: BroadcastMessage[] } = await response.json();

      try {
        const local = window.localStorage;
        local.setItem('broadcast_api_call_success', Date.now().toString());
        local.setItem(
          'broadcast_api_call_response',
          JSON.stringify({
            requestId,
            timestamp: Date.now(),
            messageCount: data.messages?.length ?? 0
          })
        );
      } catch (error) {
        console.error(`[${requestId}] Could not record API call success:`, error);
      }

      if (data.messages && data.messages.length > 0) {
        const sortedMessages = [...data.messages].sort((a, b) => {
          const timestampA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timestampB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timestampB - timestampA;
        });

        const latestMessage = sortedMessages[0];
        const messageId = latestMessage.id ?? null;

        if (messageId !== null) {
          setLastMessageId(String(messageId));
        }

        processMessage(latestMessage);
      }
    } catch (error) {
      console.error(`[${requestId}] Error fetching broadcast messages:`, error);
      try {
        const local = window.localStorage;
        local.setItem('broadcast_api_call_error', Date.now().toString());
        local.setItem('broadcast_api_call_error_message', String(error));
      } catch (storageError) {
        console.error(`[${requestId}] Could not record API call error:`, storageError);
      }
    }
  }, [lastMessageId, processMessage, userDetails?.company_name]);

  const debouncedFetch = useCallback(
    (delay: number = 300) => {
      const now = Date.now();
      if (fetchTimeoutRef.current) {
        window.clearTimeout(fetchTimeoutRef.current);
      }

      if (now - lastFetchTimeRef.current < 1000) {
        return;
      }

      fetchTimeoutRef.current = window.setTimeout(() => {
        void fetchBroadcastMessages();
      }, delay);
    },
    [fetchBroadcastMessages]
  );

  useEffect(() => {
    const initialFetchTimeout = window.setTimeout(() => {
      void fetchBroadcastMessages();

      try {
        const tabKey = `broadcast_tab_${tabIdRef.current}`;
        window.sessionStorage.setItem(tabKey, 'active');
        window.sessionStorage.setItem('broadcast_active_tab', tabIdRef.current);
      } catch (error) {
        console.error('Could not register tab:', error);
      }
    }, 500);

    try {
      const timestamp = Date.now();
      window.localStorage.setItem('page_loaded_at', timestamp.toString());
      window.localStorage.setItem('broadcast_last_fetch', timestamp.toString());
    } catch (error) {
      console.error('Could not record page load timestamp:', error);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        try {
          const tabKey = `broadcast_tab_${tabIdRef.current}`;
          window.sessionStorage.setItem(tabKey, 'active');
          window.sessionStorage.setItem('broadcast_active_tab', tabIdRef.current);
          window.sessionStorage.setItem('broadcast_last_active', Date.now().toString());
        } catch (error) {
          console.error('Could not update tab status:', error);
        }

        debouncedFetch(200);

        try {
          window.localStorage.setItem('broadcast_last_fetch', Date.now().toString());
        } catch (error) {
          console.error('Could not record visibility change timestamp:', error);
        }
      } else {
        try {
          const tabKey = `broadcast_tab_${tabIdRef.current}`;
          window.sessionStorage.setItem(tabKey, 'inactive');
          window.sessionStorage.setItem('broadcast_last_inactive', Date.now().toString());
        } catch (error) {
          console.error('Could not update tab status:', error);
        }
      }
    };

    const handlePageShow = () => {
      void fetchBroadcastMessages();
      try {
        window.localStorage.setItem('broadcast_last_fetch', Date.now().toString());
        window.localStorage.setItem('page_shown_at', Date.now().toString());
      } catch (error) {
        console.error('Could not record pageshow timestamp:', error);
      }
    };

    const handlePageHide = () => {
      try {
        window.localStorage.setItem('page_hidden_at', Date.now().toString());
      } catch (error) {
        console.error('Could not record pagehide timestamp:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, {
      passive: true
    });
    window.addEventListener('pageshow', handlePageShow, { passive: true });
    window.addEventListener('pagehide', handlePageHide, { passive: true });

    return () => {
      window.clearTimeout(initialFetchTimeout);
      if (fetchTimeoutRef.current) {
        window.clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);

      try {
        const tabKey = `broadcast_tab_${tabIdRef.current}`;
        window.sessionStorage.removeItem(tabKey);
        if (window.sessionStorage.getItem('broadcast_active_tab') === tabIdRef.current) {
          window.sessionStorage.removeItem('broadcast_active_tab');
        }
      } catch (error) {
        console.error('Could not clean up tab registration:', error);
      }
    };
  }, [debouncedFetch, fetchBroadcastMessages]);

  const refreshBroadcastMessages = useCallback(() => {
    if (fetchTimeoutRef.current) {
      window.clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    void fetchBroadcastMessages();

    try {
      const timestamp = Date.now();
      window.localStorage.setItem('broadcast_last_fetch', timestamp.toString());
      window.localStorage.setItem('manual_refresh_at', timestamp.toString());
      const tabKey = `broadcast_tab_${tabIdRef.current}`;
      window.sessionStorage.setItem(tabKey, 'active');
      window.sessionStorage.setItem('broadcast_active_tab', tabIdRef.current);
    } catch (error) {
      console.error('Could not update manual refresh timestamp:', error);
    }
  }, [fetchBroadcastMessages]);

  useEffect(() => {
    const broadcastWindow = window as BroadcastWindow;
    broadcastWindow.refreshBroadcastMessages = refreshBroadcastMessages;

    return () => {
      delete broadcastWindow.refreshBroadcastMessages;
    };
  }, [refreshBroadcastMessages]);

  const modal = useMemo(() => {
    if (!isModalVisible || !switchData) {
      return null;
    }

    return (
      <EnhancedTenantSwitchModal
        isVisible={isModalVisible}
        switchData={switchData}
        tabId={tabIdRef.current}
      />
    );
  }, [isModalVisible, switchData]);

  return (
    <>
      {children}
      {modal}
    </>
  );
};

export default BroadcastWrapper;
